import React, { useState, useEffect, useRef } from "react";
import { useController } from "react-hook-form";
import debounce from "lodash.debounce";
import api from "../api/requests";

const TikTokSearch = ({ control, name, onChange, value, required }) => {
  const {
    field: { onChange: fieldOnChange, value: fieldValue, ref },
  } = useController({
    name,
    control,
    rules: { required },
  });

  const [activeTab, setActiveTab] = useState("tiktok"); // 'tiktok' or 'upload'
  const [searchType, setSearchType] = useState("user");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [error, setError] = useState("");
  const [playingVideo, setPlayingVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Upload tab states
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [videoPreview, setVideoPreview] = useState("");
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadAuthor, setUploadAuthor] = useState("");
  const [uploadCoverImage, setUploadCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState("");
  const fileInputRef = useRef(null);
  const coverImageInputRef = useRef(null);

  // Use refs to track current values without triggering rerenders
  const stateRef = useRef({
    searchType,
    searchTerm,
    selectedUser,
    selectedVideos,
    uploadedVideo,
    activeTab,
  });

  // Update refs when state changes
  useEffect(() => {
    stateRef.current = {
      searchType,
      searchTerm,
      selectedUser,
      selectedVideos,
      uploadedVideo,
      activeTab,
    };
  });

  // Track if we're in the middle of updating
  const isUpdatingRef = useRef(false);
  // Track if we're initializing from props
  const isInitializedRef = useRef(false);

  // Initialize from fieldValue if available
  useEffect(() => {
    if (
      fieldValue &&
      typeof fieldValue === "object" &&
      !isUpdatingRef.current &&
      !isInitializedRef.current
    ) {
      try {
        isInitializedRef.current = true;
        if (fieldValue.activeTab) setActiveTab(fieldValue.activeTab);
        if (fieldValue.searchType) setSearchType(fieldValue.searchType);
        if (fieldValue.searchTerm) setSearchTerm(fieldValue.searchTerm);
        if (fieldValue.selectedUser) setSelectedUser(fieldValue.selectedUser);
        if (fieldValue.selectedVideos)
          setSelectedVideos(fieldValue.selectedVideos);
        if (fieldValue.uploadedVideo) {
          setUploadedVideo(fieldValue.uploadedVideo);
          // Create preview URL if the uploaded video has a file property
          if (fieldValue.uploadedVideo.file instanceof File) {
            setVideoPreview(URL.createObjectURL(fieldValue.uploadedVideo.file));
          }
        }
      } catch (err) {
        console.error("Error parsing initial value:", err);
      }
    }
  }, [fieldValue]);

  // Notify parent of value changes when state changes
  const notifyParent = () => {
    if (isUpdatingRef.current) return;

    isUpdatingRef.current = true;

    const currentState = stateRef.current;

    // Format the value to be passed to parent forms
    let formValue = {
      activeTab: currentState.activeTab,
      searchType: currentState.searchType,
      searchTerm: currentState.searchTerm,
      selectedUser: currentState.selectedUser,
      selectedVideos: currentState.selectedVideos,
      videoSource: currentState.activeTab, // 'tiktok' or 'upload'
    };

    // For TikTok videos
    if (
      currentState.activeTab === "tiktok" &&
      currentState.selectedVideos.length > 0
    ) {
      formValue = {
        ...formValue,
        tiktokVideo: currentState.selectedVideos[0],
      };
    }

    // For uploaded videos
    if (currentState.activeTab === "upload" && currentState.uploadedVideo) {
      formValue = {
        ...formValue,
        uploadedVideo: {
          ...currentState.uploadedVideo,
          title: uploadTitle,
          author: uploadAuthor,
        },
        coverImage: uploadCoverImage,
      };
    }

    fieldOnChange(formValue);
    if (onChange) onChange(formValue);

    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  };

  // Update when state changes
  useEffect(() => {
    notifyParent();
  }, [
    activeTab,
    searchType,
    selectedUser,
    selectedVideos,
    uploadedVideo,
    uploadTitle,
    uploadAuthor,
    uploadCoverImage,
  ]);

  // Don't trigger for every keystroke in search term
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== stateRef.current.searchTerm) {
        notifyParent();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Clean up video preview URL when component unmounts or when new video is uploaded
  useEffect(() => {
    return () => {
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  // Effect to reset videos and selected user when search term changes
  useEffect(() => {
    if (searchType === "user") {
      setSelectedUser(null);
      setVideos([]);
      setSelectedVideos([]);
    }
  }, [searchTerm, searchType]);

  // Debounced search function
  const debouncedSearch = useRef(
    debounce(async (term, type) => {
      if (!term || term.length < 2) {
        setOptions([]);
        setVideos([]);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        if (type === "user") {
          const response = await api.tiktok.searchUsers({ keywords: term });

          // Process user data from response
          // The API returns users in data.data.user_list array, where each item has a 'user' property
          const userList = response.data.user_list || [];

          // Map the user data to a simpler format
          const users = userList.map((item, index) => {
            const user = item.user || {};
            return {
              id: user.id || `user-${index}-${Date.now()}`,
              author: user.uniqueId || "",
              nickname: user.nickname || "",
              avatar: user.avatarMedium || user.avatarThumb || "",
              verified: user.verified || false,
              followerCount: item.stats?.followerCount || 0,
            };
          });

          setOptions(users);
        } else {
          const response = await api.tiktok.searchVideos({ keywords: term });

          // Format might need to be adjusted based on the actual response
          const videoList = response.data.data.videos || [];

          // Map videos to a consistent format
          const videos = videoList.map((video, index) => ({
            id:
              video.id ||
              video.aweme_id ||
              video.video_id ||
              `video-${index}-${Date.now()}`,
            title: video.title || video.desc || "Untitled",
            cover: video.cover || video.dynamicCover || video.originCover || "",
            duration: video.duration
              ? `${Math.floor(video.duration / 60)}:${(video.duration % 60)
                  .toString()
                  .padStart(2, "0")}`
              : "0:00",
            views: video.playCount || video.play_count || 0,
            author: video.author?.unique_id || "",
            authorAvatar: video.author?.avatar || "",
            description: video.desc || video.description || "",
            playUrl: video.play || video.wmplay || "",
          }));

          setVideos(videos);
        }
      } catch (err) {
        console.error("Search error:", err);
        setError(err.message || "Error performing search");
      } finally {
        setIsLoading(false);
      }
    }, 500)
  ).current;

  // Clean up the debounced function when component unmounts
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Trigger search when search term or type changes
  useEffect(() => {
    if (searchType === "video") {
      debouncedSearch(searchTerm, "video");
    } else if (searchTerm) {
      debouncedSearch(searchTerm, "user");
    }
  }, [searchTerm, searchType, debouncedSearch]);

  // Fetch user videos when a user is selected
  const fetchUserVideos = async (userId) => {
    if (!userId) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await api.tiktok.getUserPosts(userId, {});

      // Format according to the actual response structure
      // The API returns videos in data.data.videos array
      const videosList = response.data.data.videos || [];

      // Map videos to a consistent format
      const videos = videosList.map((video, index) => ({
        id: video.aweme_id || video.video_id || `video-${index}-${Date.now()}`,
        title: video.title || "Untitled",
        cover:
          video.cover || video.origin_cover || video.ai_dynamic_cover || "",
        duration: video.duration
          ? `${Math.floor(video.duration / 60)}:${(video.duration % 60)
              .toString()
              .padStart(2, "0")}`
          : "0:00",
        views: video.play_count || 0,
        description: video.title || "",
        author: video.author?.unique_id || "",
        authorAvatar: video.author?.avatar || "",
        create_time: video.create_time,
        digg_count: video.digg_count || 0,
        playUrl: video.play || video.wmplay || "",
      }));

      console.log("videos", videos);

      setVideos(videos);
    } catch (err) {
      console.error("Error fetching user videos:", err);
      setError(err.message || "Error fetching user videos");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSelectedVideos([]);
    fetchUserVideos(user.id);
  };

  // Handle input change
  const handleSearchInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    // Reset selected user, videos, and selections when search term changes
    if (searchType === "user" && selectedUser) {
      setSelectedUser(null);
      setVideos([]);
      setSelectedVideos([]);
    }
  };

  // Toggle video selection
  const toggleVideoSelection = (video) => {
    setSelectedVideos((prevSelected) => {
      if (prevSelected.some((v) => v.id === video.id)) {
        return prevSelected.filter((v) => v.id !== video.id);
      } else {
        // Allow only one selection at a time
        return [video];
      }
    });
  };

  // Handle video play
  const handlePlayVideo = (e, video) => {
    e.stopPropagation(); // Prevent video selection when clicking play button
    setPlayingVideo(video);
    setShowVideoModal(true);
  };

  // Handle closing video modal
  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setPlayingVideo(null);
  };

  // Handle search type change
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setSearchTerm("");
    setOptions([]);
    setSelectedUser(null);
    setVideos([]);
    setSelectedVideos([]);
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Only accept video files
    if (!file.type.startsWith("video/")) {
      setError("Please select a valid video file");
      return;
    }

    // Simulate upload progress
    setIsUploading(true);
    setUploadProgress(0);

    // Create video preview
    const previewURL = URL.createObjectURL(file);
    setVideoPreview(previewURL);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          // Create uploaded video object
          const videoMetadata = {
            id: `upload-${Date.now()}`,
            title: file.name,
            size: file.size,
            type: file.type,
            file: file,
            localUrl: previewURL,
          };
          setUploadedVideo(videoMetadata);
          return 100;
        }
        return prev + 5;
      });
    }, 100);
  };

  // Handle removing uploaded video
  const handleRemoveUpload = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoPreview("");
    setUploadedVideo(null);
    setUploadProgress(0);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle cover image upload
  const handleCoverImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Only accept image files
    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    // Create image preview
    const previewURL = URL.createObjectURL(file);
    setCoverImagePreview(previewURL);
    setUploadCoverImage(file);
  };

  // Handle removing cover image
  const handleRemoveCoverImage = () => {
    if (coverImagePreview) {
      URL.revokeObjectURL(coverImagePreview);
    }
    setCoverImagePreview("");
    setUploadCoverImage(null);

    // Reset file input
    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = "";
    }
  };

  // Clean up cover image preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (coverImagePreview) {
        URL.revokeObjectURL(coverImagePreview);
      }
    };
  }, [coverImagePreview]);

  // Close modal when ESC key is pressed
  useEffect(() => {
    const handleEscKeyPress = (event) => {
      if (event.key === "Escape" && showVideoModal) {
        handleCloseVideoModal();
      }
    };

    document.addEventListener("keydown", handleEscKeyPress);
    return () => {
      document.removeEventListener("keydown", handleEscKeyPress);
    };
  }, [showVideoModal]);

  return (
    <div className="space-y-4">
      {/* Tabs Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            className={`${
              activeTab === "tiktok"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-2 px-4 border-b-2 font-medium text-sm`}
            onClick={() => setActiveTab("tiktok")}
            type="button"
          >
            TikTok Search
          </button>
          <button
            className={`${
              activeTab === "upload"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-2 px-4 border-b-2 font-medium text-sm`}
            onClick={() => setActiveTab("upload")}
            type="button"
          >
            Upload Video
          </button>
        </nav>
      </div>

      {/* TikTok Search Tab */}
      {activeTab === "tiktok" && (
        <div>
          {/* Search Type Selection */}
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio"
                name={`${name}-searchType`}
                checked={searchType === "user"}
                onChange={() => handleSearchTypeChange("user")}
              />
              <span className="ml-2">User Name</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio"
                name={`${name}-searchType`}
                checked={searchType === "video"}
                onChange={() => handleSearchTypeChange("video")}
              />
              <span className="ml-2">Video</span>
            </label>
          </div>

          {/* Search Input */}
          <div className="mt-4">
            <input
              type="text"
              className="border border-gray-300 rounded-md px-4 py-2 w-full"
              placeholder={
                searchType === "user"
                  ? "Search TikTok users..."
                  : "Search TikTok videos..."
              }
              value={searchTerm}
              onChange={handleSearchInputChange}
              ref={ref}
            />
          </div>

          {/* Search Results - Users */}
          {searchType === "user" && options.length > 0 && !selectedUser && (
            <div className="mt-2 border border-gray-200 rounded-md max-h-60 overflow-y-auto">
              {options.map((user, index) => (
                <div
                  key={user.id || `user-${index}`}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                  onClick={() => handleUserSelect(user)}
                >
                  {user.avatar && (
                    <img
                      src={user.avatar}
                      alt={user.username}
                      className="w-8 h-8 rounded-full mr-2"
                    />
                  )}
                  <div>
                    <div className="font-semibold">{user.username}</div>
                    {user.nickname && (
                      <div className="text-sm text-gray-500">
                        {user.nickname}
                      </div>
                    )}
                    {user.followerCount > 0 && (
                      <div className="text-xs text-gray-400">
                        {user.followerCount.toLocaleString()} followers
                      </div>
                    )}
                    {user.verified && (
                      <span className="text-blue-500 text-xs">✓ Verified</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selected User */}
          {searchType === "user" && selectedUser && (
            <div className="mt-2 flex items-center bg-gray-100 p-2 rounded-md">
              {selectedUser.avatar && (
                <img
                  src={selectedUser.avatar}
                  alt={selectedUser.username}
                  className="w-8 h-8 rounded-full mr-2"
                />
              )}
              <div className="flex-1">
                <div className="font-semibold">{selectedUser.username}</div>
                {selectedUser.nickname && (
                  <div className="text-sm text-gray-500">
                    {selectedUser.nickname}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="text-red-500 hover:text-red-700"
                onClick={() => {
                  setSelectedUser(null);
                  setVideos([]);
                  setSelectedVideos([]);
                }}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
          )}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="text-center py-2">
              <svg
                className="animate-spin h-5 w-5 mx-auto text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}

          {/* Error Message */}
          {error && <div className="text-red-500 text-sm mt-1">{error}</div>}

          {/* Videos Grid */}
          {videos.length > 0 && (
            <div className="mt-4">
              <h3 className="font-medium mb-2">
                {searchType === "user" && selectedUser
                  ? `Videos from ${selectedUser.username}`
                  : "Search Results"}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {videos.map((video, index) => (
                  <div
                    key={video.id || `video-${index}`}
                    className="relative group"
                  >
                    <div
                      className={`border rounded-md overflow-hidden cursor-pointer ${
                        selectedVideos.some((v) => v.id === video.id)
                          ? "border-blue-500 ring-2 ring-blue-500"
                          : "border-gray-200"
                      }`}
                      onClick={() => toggleVideoSelection(video)}
                    >
                      <div className="aspect-video bg-gray-100 relative">
                        {video.cover && (
                          <img
                            src={video.cover}
                            alt={video.title || "TikTok video"}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 rounded">
                          {video.duration || "0:00"}
                        </div>

                        {/* Play button overlay */}
                        {video.playUrl && (
                          <button
                            type="button"
                            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => handlePlayVideo(e, video)}
                          >
                            <div className="w-12 h-12 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </button>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-sm font-medium truncate">
                          {video.title || "Untitled"}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {video.views.toLocaleString()} views
                        </p>
                        {video.digg_count > 0 && (
                          <p className="text-xs text-gray-500">
                            {video.digg_count.toLocaleString()} likes
                          </p>
                        )}
                      </div>

                      {/* Checkbox overlay */}
                      <div
                        className="absolute top-2 left-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="form-checkbox h-5 w-5"
                          checked={selectedVideos.some(
                            (v) => v.id === video.id
                          )}
                          onChange={(e) => {
                            toggleVideoSelection(video);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Videos Count */}
          {selectedVideos.length > 0 && (
            <div className="mt-4 text-sm font-medium">
              {selectedVideos.length} video
              {selectedVideos.length !== 1 ? "s" : ""} selected
            </div>
          )}
        </div>
      )}

      {/* Upload Video Tab */}
      {activeTab === "upload" && (
        <div className="space-y-4">
          {!uploadedVideo ? (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center"
              onClick={() => !isUploading && fileInputRef.current.click()}
            >
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                ></path>
              </svg>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-medium text-blue-600 hover:text-blue-500">
                  Upload a video
                </span>{" "}
                or drag and drop
              </p>
              <p className="mt-1 text-xs text-gray-500">
                MP4, MOV, WEBM up to 100MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleFileUpload}
                disabled={isUploading}
              />
              {isUploading && (
                <div className="w-full mt-4">
                  <div className="bg-gray-200 rounded-full h-2.5 w-full">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Uploading: {uploadProgress}%
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border rounded-lg overflow-hidden max-w-md mx-auto">
                <div className="relative aspect-video bg-black w-full max-w-md">
                  {videoPreview && (
                    <video
                      src={videoPreview}
                      className="w-full h-full"
                      controls
                    ></video>
                  )}
                  <button
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    onClick={handleRemoveUpload}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </div>
                <div className="p-3 border-t">
                  <p className="font-medium text-sm">{uploadedVideo.title}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((uploadedVideo.size / 1024 / 1024) * 10) / 10}{" "}
                    MB
                  </p>
                </div>
              </div>

              {/* Additional Fields */}
              <div className="space-y-4 max-w-md mx-auto">
                {/* Title Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Video Title
                  </label>
                  <input
                    type="text"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter video title"
                  />
                </div>

                {/* Author Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author
                  </label>
                  <input
                    type="text"
                    value={uploadAuthor}
                    onChange={(e) => setUploadAuthor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter author"
                  />
                </div>

                {/* Cover Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cover Image
                  </label>
                  {!coverImagePreview ? (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer"
                      onClick={() => coverImageInputRef.current.click()}
                    >
                      <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">
                        Click to upload cover image
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 5MB
                      </p>
                      <input
                        ref={coverImageInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleCoverImageUpload}
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={coverImagePreview}
                        alt="Cover preview"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <button
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        onClick={handleRemoveCoverImage}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
        </div>
      )}

      {/* Video Player Modal */}
      {showVideoModal && playingVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden my-8">
            <button
              type="button"
              className="absolute top-2 right-2 text-white hover:text-gray-300 z-10"
              onClick={handleCloseVideoModal}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="aspect-video w-full max-h-[80vh]">
              {playingVideo.playUrl ? (
                <video
                  src={playingVideo.playUrl}
                  controls
                  autoPlay
                  className="w-full h-full"
                  controlsList="nodownload"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                  Video playback not available
                </div>
              )}
            </div>

            <div className="p-4 bg-black text-white">
              <h3 className="text-lg font-medium">{playingVideo.title}</h3>
              <div className="flex items-center text-sm text-gray-400 mt-1">
                <span>{playingVideo.views?.toLocaleString() || 0} views</span>
                <span className="mx-2">•</span>
                <span>
                  {playingVideo.digg_count?.toLocaleString() || 0} likes
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TikTokSearch;
